import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Modal, Space, Table, Tag, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createSubAccount,
  disableSubAccount,
  enableSubAccount,
  getOperationLogs,
  getSubAccountDetail,
  getSubAccountList,
  removeSubAccount,
  updateSubAccountPermissions,
} from "../../service/permission";
import { adaptSubAccountList } from "../../utils/identity";
import { message } from "../../components/Message";
import SubAccountFormModal from "../../components/SubAccountFormModal";
import PermissionTagGroup from "../../components/PermissionTagGroup";
import NoPermission from "../../components/NoPermission";
import styles from "./index.modules.less";
import { LeftOutlined } from "@ant-design/icons";

const PermissionManage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permission = useSelector((state) => state.permission);
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [permissionModules, setPermissionModules] = useState([]);
  const [quotaState, setQuotaState] = useState({
    subAccountQuota: 20,
    subAccountCurrentCount: 0,
    subAccountQuotaReached: false,
    canAdd: true,
  });

  const isOwner = permission.identityType === "owner";

  const loadList = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await getSubAccountList({ p_num: page, p_size: pageSize, status: "all" });
      const adapted = adaptSubAccountList(response || {});
      setListData(adapted.list);
      setPagination({ current: page, pageSize, total: adapted.total });
      setPermissionModules(adapted.permissionModules || []);
      setQuotaState({
        subAccountQuota: adapted.subAccountQuota,
        subAccountCurrentCount: adapted.subAccountCurrentCount,
        subAccountQuotaReached: adapted.subAccountQuotaReached,
        canAdd: adapted.canAdd,
      });
      dispatch.permission.setState({
        subAccountQuota: adapted.subAccountQuota,
        subAccountCurrentCount: adapted.subAccountCurrentCount,
        subAccountQuotaReached: adapted.subAccountQuotaReached,
      });
    } catch (error) {
      message.show({
        type: "error",
        content: error?.errmsg || "子账号列表加载失败",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      loadList(1, pagination.pageSize);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(
    () => [
      {
        title: "360账号",
        dataIndex: "qidMasked",
        key: "qidMasked",
      },
      {
        // title: "昵称/备注",
        title: "备注",
        dataIndex: "nickname",
        key: "nickname",
        render: (value) => {
          if (!value) return "--";
          return (
            <Tooltip title={value}>
              <span>{value}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "权限模块",
        dataIndex: "permissions",
        key: "permissions",
        render: (_, record) => <PermissionTagGroup permissions={record.permissions} />,
      },
      {
        title: "添加时间",
        dataIndex: "createdAt",
        key: "createdAt",
      },
      {
        title: "最近登录",
        dataIndex: "lastLoginAt",
        key: "lastLoginAt",
        render: (value) => value || "--",
      },
      {
        title: "状态",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          if (status === "active") return <Tag color="green">正常</Tag>;
          if (status === "removed") return <Tag color="default">已移除</Tag>;
          return <Tag color="orange">已禁用</Tag>;
        },
      },
      {
        title: "操作",
        key: "action",
        render: (_, record) => {
          if (record.status === "removed") {
            return (
              <Button type="link" onClick={() => handleOpenAdd()}>重新添加</Button>
            );
          }
          return (
            <Space>
              <Button type="link" onClick={() => handleOpenEdit(record)}>编辑权限</Button>
              <Button type="link" onClick={() => handleToggleStatus(record)}>
                {record.status === "active" ? "禁用" : "启用"}
              </Button>
              <Button type="link" danger onClick={() => handleRemove(record)}>移除</Button>
            </Space>
          );
        },
      },
    ],
    []
  );

  const handleOpenAdd = () => {
    if (quotaState.subAccountQuotaReached || quotaState.canAdd === false) {
      message.show({
        type: "error",
        content: `已达子账号数量上限（${quotaState.subAccountQuota}个），如需增加请联系管理员`,
        closeTime: 2000,
      });
      return;
    }
    setEditingRecord(null);
    setFormMode("add");
    setFormOpen(true);
  };

  const handleOpenEdit = async (record) => {
    setSubmitting(true);
    try {
      const detail = await getSubAccountDetail({ id: record.id });
      setEditingRecord({
        ...record,
        ...(detail || {}),
        permissions: detail?.permissions || record.permissions || [],
        nickname: detail?.nickname ?? record.nickname,
        account: detail?.account || detail?.qid || record.qid || record.qidMasked,
      });
      setFormMode("edit");
      setFormOpen(true);
    } catch (error) {
      message.show({
        type: "error",
        content: error?.errmsg || "子账号详情加载失败，请稍后重试",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (formMode === "add") {
        await createSubAccount({
          account: values.account,
          nickname: values.nickname || "",
          permissions: values.permissions || [],
        });
      } else {
        await updateSubAccountPermissions({
          id: editingRecord.id,
          permissions: values.permissions || [],
          nickname: values.nickname || "",
        });
      }
      setFormOpen(false);
      await loadList(pagination.current, pagination.pageSize);
      message.show({
        type: "success",
        content: formMode === "add" ? "子账号添加成功" : "子账号权限更新成功",
      });
    } catch (error) {
      message.show({
        type: "error",
        content: error?.errmsg || (formMode === "add" ? "子账号添加失败" : "子账号权限更新失败"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = (record) => {
    const isDisable = record.status === "active";
    Modal.confirm({
      title: isDisable ? "确认禁用该子账号？" : "确认启用该子账号？",
      content: `${record.qidMasked} 将被${isDisable ? "禁用" : "启用"}。`,
      onOk: async () => {
        try {
          if (isDisable) {
            await disableSubAccount({ id: record.id, reason: "" });
          } else {
            await enableSubAccount({ id: record.id });
          }
          await loadList(pagination.current, pagination.pageSize);
          message.show({
            type: "success",
            content: isDisable ? "已禁用子账号" : "已启用子账号",
          });
        } catch (error) {
          message.show({
            type: "error",
            content: error?.errmsg || "操作失败，请稍后重试",
          });
        }
      },
    });
  };

  const handleRemove = (record) => {
    Modal.confirm({
      title: "确认移除该子账号？",
      content: `移除后将解除 ${record.qidMasked} 与当前厂商的绑定关系。`,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeSubAccount({ id: record.id });
          await loadList(1, pagination.pageSize);
          message.show({
            type: "success",
            content: "子账号已移除",
          });
        } catch (error) {
          message.show({
            type: "error",
            content: error?.errmsg || "移除失败，请稍后重试",
          });
        }
      },
    });
  };

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logPagination, setLogPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const MODULE_LABEL = {
    app_management: "软件管理",
    complaint_handling: "客诉处理",
    data_service: "数据服务",
  };

  const RESULT_LABEL = {
    success: "成功",
    failed: "失败",
  };

  const loadLogs = async (page = 1, pageSize = 20) => {
    setLogLoading(true);
    try {
      const now = new Date();
      const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} 23:59:59`;
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const start = `${past.getFullYear()}-${String(past.getMonth() + 1).padStart(2, "0")}-${String(past.getDate()).padStart(2, "0")} 00:00:00`;

      const response = await getOperationLogs({
        start_time: start,
        end_time: end,
        page,
        page_size: pageSize,
      });
      setLogData(response?.list || []);
      const totalFromResponse = response?.total_num ?? response?.total ?? response?.totalNum ?? 0;
      setLogPagination({
        current: response?.page || response?.p_num || page,
        pageSize: response?.page_size || response?.p_size || pageSize,
        total: totalFromResponse,
      });
    } catch (error) {
      message.show({
        type: "error",
        content: error?.errmsg || "操作日志加载失败",
      });
    } finally {
      setLogLoading(false);
    }
  };

  const handleOpenLogModal = () => {
    setLogModalOpen(true);
    loadLogs(1, 20);
  };

  const logColumns = [
    {
      title: "操作时间",
      dataIndex: "operate_time",
      key: "operate_time",
      width: 170,
    },
    {
      title: "操作人",
      dataIndex: "operator_display",
      key: "operator_display",
      width: 150,
    },
    {
      title: "身份类型",
      dataIndex: "identity_type",
      key: "identity_type",
      width: 90,
      render: (val) => <Tag color={val === 1 ? "gold" : "blue"}>{val === 1 ? "主账号" : "子账号"}</Tag>,
    },
    {
      title: "模块",
      dataIndex: "module",
      key: "module",
      width: 100,
      render: (val) => MODULE_LABEL[val] || val || "--",
    },
    {
      title: "操作类型",
      dataIndex: "action",
      key: "action",
      width: 150,
    },
    {
      title: "操作对象",
      dataIndex: "target_type",
      key: "target_type",
      width: 100,
      render: (val) => val || "--",
    },
    {
      title: "结果",
      dataIndex: "result",
      key: "result",
      width: 70,
      render: (val) => <Tag color={val === "success" ? "green" : "red"}>{RESULT_LABEL[val] || val}</Tag>,
    },
    {
      title: "详情",
      dataIndex: "detail",
      key: "detail",
      render: (val) => {
        if (!val) return "--";
        let text;
        try {
          const parsed = JSON.parse(val);
          text = JSON.stringify(parsed);
        } catch {
          text = val;
        }
        return (
          <Tooltip title={text}>
            <div className={styles.logDetailCell}>{text}</div>
          </Tooltip>
        );
      },
    },
  ];

  if (!isOwner) {
    return <NoPermission description="仅主账号可管理子账号权限" />;
  }

  return (
    <div className={styles.page}>
      <Card
        title={
          <Space size={12} align="center">
            <Button type="link" onClick={() => navigate(-1)} className={styles.backBtn}>
              <LeftOutlined />返回
            </Button>
            <span className={styles.titleDivider} />
            <span>权限管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button onClick={handleOpenLogModal}>操作日志</Button>
            <Tooltip title={quotaState.subAccountQuotaReached || quotaState.canAdd === false ? `已达子账号数量上限（${quotaState.subAccountQuota}个），如需增加请联系管理员` : ""}>
              <Button type="primary" onClick={handleOpenAdd} disabled={quotaState.subAccountQuotaReached || quotaState.canAdd === false}>添加子账号</Button>
            </Tooltip>
          </Space>
        }
      >
        <div className={styles.quota}>
          当前已添加 {quotaState.subAccountCurrentCount} / {quotaState.subAccountQuota} 个子账号
        </div>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={listData}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (page, pageSize) => loadList(page, pageSize),
          }}
        />
      </Card>
      <SubAccountFormModal
        open={formOpen}
        mode={formMode}
        loading={submitting}
        initialValues={editingRecord}
        permissionModules={permissionModules}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <Modal
        title="操作日志"
        open={logModalOpen}
        onCancel={() => setLogModalOpen(false)}
        footer={null}
        width={1200}
        destroyOnClose
        className={styles.logModal}
      >
        <Table
          rowKey="id"
          loading={logLoading}
          columns={logColumns}
          dataSource={logData}
          scroll={{ x: 1000, y: 400 }}
          pagination={{
            current: logPagination.current,
            pageSize: logPagination.pageSize,
            total: logPagination.total,
            showSizeChanger: false,
          }}
          onChange={(pag) => loadLogs(pag.current, pag.pageSize)}
        />
      </Modal>
    </div>
  );
};

export default PermissionManage;
